/**
 * Site header — renders sidebar trigger, optional page context (title, subtitle link,
 * share action, schedule action) from the page-context store, and theme toggle.
 */

import { Link } from "@tanstack/react-router";
import { Link2 } from "lucide-react";
import { useState } from "react";
import { Clock } from "@/components/animate-ui/icons/clock";
import { ShareDialog } from "@/components/share-dialog";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useSchedules } from "@/lib/api/hooks/tasks";
import { usePageContextStore } from "@/stores/page-context-store";

export function SiteHeader() {
	const {
		title,
		subtitle,
		subtitleLink,
		shareRunId,
		shareToken,
		scheduleAction,
		requestScheduleModal,
	} = usePageContextStore();
	const [shareDialogOpen, setShareDialogOpen] = useState(false);
	const { data: schedulesData } = useSchedules();
	const schedules = schedulesData?.schedules || [];
	const hasSchedule = scheduleAction
		? schedules.some((s) => s.task_id === scheduleAction.taskId)
		: false;
	const isShared = !!shareToken;

	return (
		<header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
			<div className="flex w-full items-center justify-between gap-1 px-4 lg:gap-2 lg:px-6">
				<div className="flex min-w-0 flex-1 items-center gap-1 lg:gap-2">
					<SidebarTrigger className="-ml-1 flex-shrink-0" />

					{title && (
						<>
							<Separator
								orientation="vertical"
								className="mx-2 flex-shrink-0 data-[orientation=vertical]:h-4"
							/>
							<Tooltip delayDuration={300}>
								<TooltipTrigger asChild>
									<span className="truncate text-sm font-medium max-w-[40vw] sm:max-w-[50vw] lg:max-w-[65vw]">
										{title}
									</span>
								</TooltipTrigger>
								<TooltipContent variant="subtle">{title}</TooltipContent>
							</Tooltip>
							{subtitle && subtitleLink && (
								<>
									<span className="flex-shrink-0 text-muted-foreground text-sm">·</span>
									<Tooltip delayDuration={300}>
										<TooltipTrigger asChild>
											<Link
												to={subtitleLink}
												className="truncate text-sm text-muted-foreground hover:underline"
											>
												{subtitle}
											</Link>
										</TooltipTrigger>
										<TooltipContent variant="subtle">{subtitle}</TooltipContent>
									</Tooltip>
								</>
							)}
							<div className="flex flex-shrink-0 items-center">
								{scheduleAction && (
									<Tooltip delayDuration={300}>
										<TooltipTrigger asChild>
											<Button
												variant="ghost"
												size="icon"
												onClick={requestScheduleModal}
												aria-label={hasSchedule ? "Edit schedule" : "Set schedule"}
												className="h-7 w-7"
											>
												<Clock animateOnHover className="h-4 w-4" />
											</Button>
										</TooltipTrigger>
										<TooltipContent variant="subtle">
											{hasSchedule ? "Edit schedule" : "Set schedule"}
										</TooltipContent>
									</Tooltip>
								)}
								{shareRunId && (
									<>
										<Tooltip delayDuration={300}>
											<TooltipTrigger asChild>
												<Button
													variant="ghost"
													size="icon"
													onClick={() => setShareDialogOpen(true)}
													aria-label={isShared ? "Manage share link" : "Share conversation"}
													className="h-7 w-7"
												>
													<Link2 className="h-4 w-4" />
												</Button>
											</TooltipTrigger>
											<TooltipContent variant="subtle">
												{isShared ? "Manage share link" : "Create a public link"}
											</TooltipContent>
										</Tooltip>
										<ShareDialog
											open={shareDialogOpen}
											onOpenChange={setShareDialogOpen}
											runId={shareRunId}
											shareToken={shareToken}
										/>
									</>
								)}
							</div>
						</>
					)}
				</div>

				<ThemeToggle />
			</div>
		</header>
	);
}
